import type { PlayerDto, PurchaseItem, ReviveOfferResponse } from "../api/dto/player.dto.js";
import type { CumulativeBoosterInventoryDto } from "../api/dto/cumulative_boosters.dto.js";
import { ApiError } from "../api/http_client.js";
import { getContentConfig } from "../api/requests/player.requests.js";
import { AuthSessionStore } from "../api/auth_session.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { CumulativeBoosterService } from "../cumulative_boosters/cumulative_booster_service.js";
import { PersistentCache } from "./persistent_cache.js";
import { ServerClock } from "./server_clock.js";
import {
  ECONOMY_DEFAULTS,
  difficultyName,
  resolveLevelRewardCoins,
  resolveReviveOfferFromConfig,
  type EconomyConfig,
  type LevelDifficultyName,
} from "./economy_resolvers.js";

export { ECONOMY_DEFAULTS, LEVEL_DIFFICULTY_NAMES, difficultyName, resolveLevelRewardCoins, resolveReviveOfferFromConfig } from "./economy_resolvers.js";
export type { EconomyConfig, LevelDifficultyName } from "./economy_resolvers.js";

export const OFFLINE_ECONOMY_CONFIG_TYPES = [ "economy", "level_rewards", "revive" ] as const;

interface OfflineConfigsSnapshot
{
  economy: Partial<EconomyConfig> | null;
  level_rewards: unknown;
  revive: unknown;
  fetched_at: number;
}

const CONFIGS_KEY = "economy_configs_v1";

function isObject( value: unknown ): value is Record<string, unknown>
{
  return !!value && typeof value === "object" && !Array.isArray( value );
}

function positiveNumber( value: unknown, fallback: number ): number
{
  return typeof value === "number" && Number.isFinite( value ) && value > 0 ? value : fallback;
}

export class OfflineEconomy
{
  private static snapshot: OfflineConfigsSnapshot = OfflineEconomy.readSnapshot();
  private static refreshing: Promise<void> | null = null;

  public static getEconomyConfig(): EconomyConfig
  {
    const stats = StatisticsStorage.load();
    const raw = this.snapshot.economy ?? {};
    return {
      max_hearts: positiveNumber( raw.max_hearts, Math.max( stats.max_hearts, 1 ) ),
      heart_regen_minutes: positiveNumber( raw.heart_regen_minutes, ECONOMY_DEFAULTS.heart_regen_minutes ),
      coins_per_star: positiveNumber( raw.coins_per_star, ECONOMY_DEFAULTS.coins_per_star ),
      continue_balls_price: positiveNumber( raw.continue_balls_price, ECONOMY_DEFAULTS.continue_balls_price ),
      fireworks_price: positiveNumber( raw.fireworks_price, stats.fireworks_price ),
      fireworks_pack_qty: positiveNumber( raw.fireworks_pack_qty, stats.fireworks_pack_qty ),
      heart_refill_price: positiveNumber( raw.heart_refill_price, stats.heart_refill_price ),
    };
  }

  public static hasCachedConfig( type: string ): boolean
  {
    const stored = PersistentCache.read<Partial<OfflineConfigsSnapshot>>( CONFIGS_KEY );
    if ( !stored || typeof stored !== "object" ) return false;
    const value = ( stored as Record<string, unknown> )[type];
    return value !== null && value !== undefined;
  }

  public static refreshConfigs( only?: readonly string[] ): Promise<void>
  {
    if ( this.refreshing ) return this.refreshing;
    const wanted = new Set( only && only.length > 0 ? only : OFFLINE_ECONOMY_CONFIG_TYPES );

    this.refreshing = ( async () =>
    {
      const fetchOne = async ( type: string ): Promise<unknown | null> =>
      {
        try
        {
          return await getContentConfig<unknown>( type );
        }
        catch ( e )
        {
          if ( e instanceof ApiError && e.status === 404 ) return null;
          throw e;
        }
      };

      try
      {
        const fetchIfWanted = ( type: string, current: unknown ): Promise<unknown | null> =>
          wanted.has( type ) ? fetchOne( type ) : Promise.resolve( current );
        const settled = await Promise.allSettled( [
          fetchIfWanted( "economy", this.snapshot.economy ),
          fetchIfWanted( "level_rewards", this.snapshot.level_rewards ),
          fetchIfWanted( "revive", this.snapshot.revive ),
        ] );
        const current = [ this.snapshot.economy, this.snapshot.level_rewards, this.snapshot.revive ];
        const [ economy, level_rewards, revive ] = settled.map( ( r, i ) => r.status === "fulfilled" ? r.value : current[i] );
        const failed = settled.find( r => r.status === "rejected" ) as PromiseRejectedResult | undefined;
        this.snapshot = {
          economy: isObject( economy ) ? economy as Partial<EconomyConfig> : null,
          level_rewards,
          revive,
          fetched_at: Date.now(),
        };
        PersistentCache.write( CONFIGS_KEY, this.snapshot );
        console.log( "[OfflineEconomy] configs refreshed" );
        if ( failed ) throw failed.reason;
      }
      catch ( e )
      {
        console.warn( "[OfflineEconomy] config refresh failed, keeping cached", e );
      }
      finally
      {
        this.refreshing = null;
      }
    } )();

    return this.refreshing;
  }

  public static levelRewardCoins( level: number, difficulty: LevelDifficultyName, stars: number ): number
  {
    const cfg = this.getEconomyConfig();
    return resolveLevelRewardCoins( this.snapshot.level_rewards, level, difficulty, cfg.coins_per_star * stars );
  }

  public static reviveOffer( level: number, purchase_index: number ): ReviveOfferResponse
  {
    return resolveReviveOfferFromConfig(
      this.snapshot.revive,
      level,
      purchase_index,
      this.getEconomyConfig().continue_balls_price
    );
  }

  public static regenMs(): number
  {
    return this.getEconomyConfig().heart_regen_minutes * 60_000;
  }

  public static applyHeartsRegen( now_ms = ServerClock.nowMs() ): boolean
  {
    const stats = StatisticsStorage.load();
    const max_hearts = Math.max( 1, stats.max_hearts );
    if ( stats.hearts >= max_hearts || stats.next_heart_at <= 0 || now_ms < stats.next_heart_at )
      return false;

    const regen_ms = this.regenMs();
    let hearts = stats.hearts;
    let next_heart_at = stats.next_heart_at;
    while ( hearts < max_hearts && next_heart_at > 0 && now_ms >= next_heart_at )
    {
      hearts++;
      next_heart_at = hearts >= max_hearts ? 0 : next_heart_at + regen_ms;
    }
    StatisticsStorage.update( { hearts, next_heart_at } );
    return true;
  }

  public static spendHeart(): { hearts: number; max_hearts: number; next_heart_at: number }
  {
    this.applyHeartsRegen();
    const stats = StatisticsStorage.load();
    const max_hearts = Math.max( 1, stats.max_hearts );
    const now = ServerClock.nowMs();
    if ( stats.infinite_hearts_until > now )
      return { hearts: stats.hearts, max_hearts, next_heart_at: stats.next_heart_at };

    const hearts = Math.max( 0, stats.hearts - 1 );
    const next_heart_at = stats.hearts >= max_hearts || stats.next_heart_at <= 0
      ? now + this.regenMs()
      : stats.next_heart_at;
    StatisticsStorage.update( { hearts, next_heart_at } );
    return { hearts, max_hearts, next_heart_at };
  }

  public static addHearts( amount: number ): void
  {
    if ( amount <= 0 ) return;
    const stats = StatisticsStorage.load();
    const max_hearts = Math.max( 1, stats.max_hearts );
    const hearts = Math.min( stats.hearts + amount, Math.max( stats.hearts, max_hearts ) );
    StatisticsStorage.update( {
      hearts,
      next_heart_at: hearts >= max_hearts ? 0 : stats.next_heart_at,
    } );
  }

  public static refillHearts(): void
  {
    const stats = StatisticsStorage.load();
    StatisticsStorage.update( { hearts: Math.max( stats.hearts, stats.max_hearts ), next_heart_at: 0 } );
  }

  public static addCoins( amount: number ): void
  {
    if ( amount === 0 ) return;
    const stats = StatisticsStorage.load();
    StatisticsStorage.update( { coins: Math.max( 0, stats.coins + amount ) } );
  }

  public static addBoosters( delta: Partial<CumulativeBoosterInventoryDto> ): void
  {
    const inventory = CumulativeBoosterService.getInventory();
    CumulativeBoosterService.applyInventory( {
      fireworks: inventory.fireworks + ( delta.fireworks ?? 0 ),
      multi_balls: inventory.multi_balls + ( delta.multi_balls ?? 0 ),
      big_balls: inventory.big_balls + ( delta.big_balls ?? 0 ),
      infinite_balls: inventory.infinite_balls + ( delta.infinite_balls ?? 0 ),
      mines: ( inventory.mines ?? 0 ) + ( delta.mines ?? 0 ),
      freezes: ( inventory.freezes ?? 0 ) + ( delta.freezes ?? 0 ),
    } );
    this.mirrorInventoryToStats();
  }

  public static mirrorInventoryToStats(): void
  {
    const inventory = CumulativeBoosterService.getInventory();
    StatisticsStorage.update( {
      fireworks: inventory.fireworks,
      multiballs: inventory.multi_balls,
      big_balls: inventory.big_balls,
      infinite_balls: inventory.infinite_balls,
      mines: inventory.mines ?? 0,
      freezes: inventory.freezes ?? 0,
    } );
  }

  public static restoreInventoryFromStats(): void
  {
    const stats = StatisticsStorage.load();
    CumulativeBoosterService.applyInventory( {
      fireworks: stats.fireworks,
      multi_balls: stats.multiballs,
      big_balls: stats.big_balls,
      infinite_balls: stats.infinite_balls,
      mines: stats.mines,
      freezes: stats.freezes,
    } );
  }

  public static completeLevel(
    level: number,
    stars: number,
    difficulty: LevelDifficultyName
  ): { coins: number; points: number } | null
  {
    const stats = StatisticsStorage.load();
    const rewarded = stats.rewarded_levels ?? [];
    const first_completion = !rewarded.includes( level ) && level <= stats.level;
    if ( !first_completion ) return null;

    const coins = this.levelRewardCoins( level, difficulty, stars );
    StatisticsStorage.update( {
      coins: stats.coins + coins,
      points: stats.points + stars,
      rewarded_levels: [ ...rewarded, level ],
      level: Math.max( stats.level, level + 1 ),
    } );

    return { coins, points: stars };
  }

  public static doubleLevelReward( level: number, stars: number, difficulty: LevelDifficultyName ): number
  {
    const coins = this.levelRewardCoins( level, difficulty, stars );
    this.addCoins( coins );
    return coins;
  }

  public static purchase(
    item: PurchaseItem,
    price: number,
    qty: number
  ): { ok: true; price: number; qty: number } | { ok: false; reason: "insufficient_funds" | "hearts_full" }
  {
    const stats = StatisticsStorage.load();
    if ( item === "hearts_refill" )
    {
      this.applyHeartsRegen();
      if ( StatisticsStorage.load().hearts >= stats.max_hearts )
        return { ok: false, reason: "hearts_full" };
    }
    if ( stats.coins < price )
      return { ok: false, reason: "insufficient_funds" };

    this.addCoins( -price );
    if ( item === "hearts_refill" )
      this.refillHearts();
    else if ( item === "fireworks" )
      this.addBoosters( { fireworks: qty } );

    return { ok: true, price, qty };
  }

  public static localPlayerDto(): PlayerDto
  {
    const stats = StatisticsStorage.load();
    const inventory = CumulativeBoosterService.getInventory();
    return {
      id: AuthSessionStore.getPlayerId() ?? 0,
      player_level: Math.max( 1, stats.level ),
      last_seen_level: Math.max( 1, stats.seen_level ),
      coins: stats.coins,
      points: stats.points,
      hearts: stats.hearts,
      max_hearts: stats.max_hearts,
      next_heart_at: stats.next_heart_at > 0 ? new Date( stats.next_heart_at ).toISOString() : null,
      infinite_hearts_until: stats.infinite_hearts_until > 0 ? new Date( stats.infinite_hearts_until ).toISOString() : null,
      no_ads_until: stats.no_ads_until > 0 ? new Date( stats.no_ads_until ).toISOString() : null,
      heart_refill_price: stats.heart_refill_price,
      fireworks_price: stats.fireworks_price,
      fireworks_pack_qty: stats.fireworks_pack_qty,
      rewarded_levels: [ ...stats.rewarded_levels ],
      completed_booster_tutorials: [ ...stats.completed_booster_tutorials ],
      completed_feature_tutorials: [ ...stats.completed_feature_tutorials ],
      nickname: localStorage.getItem( "player_nickname" ),
      avatar_id: Number( localStorage.getItem( "player_avatar_id" ) ?? 0 ) || 0,
      facebook_avatar_url: localStorage.getItem( "player_facebook_avatar_url" ),
      country: localStorage.getItem( "player_country" ),
      created_at: new Date( stats.install_date ).toISOString(),
      big_balls: inventory.big_balls,
      infinite_balls: inventory.infinite_balls,
      multi_balls: inventory.multi_balls,
      fireworks: inventory.fireworks,
      mines: inventory.mines ?? 0,
      freezes: inventory.freezes ?? 0,
    };
  }

  private static readSnapshot(): OfflineConfigsSnapshot
  {
    const stored = PersistentCache.read<OfflineConfigsSnapshot>( CONFIGS_KEY );
    return stored && typeof stored === "object"
      ? { economy: stored.economy ?? null, level_rewards: stored.level_rewards ?? null, revive: stored.revive ?? null, fetched_at: stored.fetched_at ?? 0 }
      : { economy: null, level_rewards: null, revive: null, fetched_at: 0 };
  }
}
